import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import axios from 'axios';
import candidateAuthService from '../../../services/candidateAuthService';

const ApplyModal = ({ job, onClose, onSuccess }) => {
    const candidate = candidateAuthService.getCurrentCandidate() || JSON.parse(localStorage.getItem('user') || '{}');

    const [formData, setFormData] = React.useState({
        name: candidate.full_name || candidate.name || '',
        email: candidate.email || '',
        phone: candidate.phone || '',
        skills: '',
        experience_years: 0,
        skill_level: 'entry',
        source: 'job_portal',
        notes: '',
        current_salary: '',
        expected_salary: '',
        notice_period: '',
        location_preferred: '',
        previous_company: '',
        previous_company_details: '',
        resume_url: ''
    });
    const [resumeFile, setResumeFile] = React.useState(null);
    const [resumePreview, setResumePreview] = React.useState(null);
    const [uploading, setUploading] = React.useState(false);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [showCustomSkillLevel, setShowCustomSkillLevel] = React.useState(false);
    const [customSkillLevel, setCustomSkillLevel] = React.useState('');

    // Points at the ERP backend's router (php -S localhost:8000 router.php)
    const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
            if (!allowedTypes.includes(file.type)) {
                setError('Please upload a PDF or Word document');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setError('File size must be less than 5MB');
                return;
            }
            setResumeFile(file);
            setResumePreview(file.name);
            setError(null);
        }
    };

    const uploadResume = async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('type', 'resume');

        let res = null;
        try {
            res = await axios.post(`${API_URL}/marketplace-upload`, formDataUpload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
        } catch (e1) {
            try {
                res = await axios.post(`${API_URL}/upload.php`, formDataUpload, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            } catch (e2) {
                res = await axios.post(`${API_URL}/upload`, formDataUpload, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }
        }

        const url = res?.data?.data?.url || res?.data?.url || res?.data?.data?.relative_url || res?.data?.relative_url;
        if (!url) {
            throw new Error(res?.data?.message || res?.data?.error || 'Failed to upload resume file');
        }
        return url;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        // Validation
        if (!formData.name.trim()) {
            setError('Name is required');
            setLoading(false);
            return;
        }
        if (!formData.phone.trim()) {
            setError('Phone number is required');
            setLoading(false);
            return;
        }

        try {
            let resumeUrl = formData.resume_url;

            if (resumeFile) {
                setUploading(true);
                try {
                    resumeUrl = await uploadResume(resumeFile);
                    setFormData(prev => ({ ...prev, resume_url: resumeUrl }));
                } catch (uploadError) {
                    console.error('Resume upload failed:', uploadError);
                    setError(uploadError.message || 'Resume upload failed. Please try again.');
                    setLoading(false);
                    setUploading(false);
                    return;
                } finally {
                    setUploading(false);
                }
            }

            const headers = candidateAuthService.getAuthHeaders();
            const response = await axios.post(`${API_URL}/marketplace-apply`, {
                ...formData,
                candidate_id: candidate?.id || undefined,
                resume_url: resumeUrl,
                job_id: job.id,
                skills: formData.skills ? formData.skills.split(',').map(s => s.trim()).filter(Boolean) : []
            }, { headers });

            if (response.data.success) {
                if (formData.email) {
                    const existingUser = candidateAuthService.getCurrentCandidate() || {};
                    const updatedUser = {
                        ...existingUser,
                        email: formData.email,
                        full_name: formData.name || existingUser.full_name || existingUser.name,
                        name: formData.name || existingUser.name || existingUser.full_name
                    };
                    localStorage.setItem('candidate_user', JSON.stringify(updatedUser));
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                }
                window.dispatchEvent(new Event('authChange'));
                window.dispatchEvent(new Event('candidateAuthChange'));
                onSuccess(response.data.data?.application_id || response.data.application_id);
            } else {
                setError(response.data.message || response.data.error || "Failed to submit application.");
            }
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || "An error occurred. Please try again.");
            if (err.response?.status === 409 || err.response?.data?.already_applied) {
                window.dispatchEvent(new Event('authChange'));
            }
        } finally {
            setLoading(false);
        }
    };

    const removeResume = () => {
        setResumeFile(null);
        setResumePreview(null);
        setFormData(prev => ({ ...prev, resume_url: '' }));
    };

    if (!job) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <div className="bg-background rounded-xl max-w-2xl w-full shadow-2xl animate-fade-in-up border border-border max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-background sticky top-0 z-10">
                    <div>
                        <h2 className="text-xl font-bold text-foreground">Apply for Position</h2>
                        <p className="text-sm text-muted-foreground line-clamp-1">{job.title} at {job.company}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
                    >
                        <Icon name="X" size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                    <div className="space-y-6">
                        {error && (
                            <div className="p-3 bg-error/10 border border-error/20 rounded-lg text-error text-sm flex items-center gap-2">
                                <Icon name="AlertCircle" size={16} />
                                {error}
                            </div>
                        )}

                        {/* Basic Information */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-sm font-medium text-foreground">Full Name *</label>
                                <input
                                    required
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Enter your full name"
                                    className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-foreground">Phone Number *</label>
                                <input
                                    required
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+91-9876543210"
                                    className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-foreground">Email Address</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="your.email@example.com"
                                    className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                />
                            </div>
                        </div>

                        {/* Professional Information */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="text-sm font-medium text-foreground">Experience (Years)</label>
                                <input
                                    type="number"
                                    name="experience_years"
                                    value={formData.experience_years}
                                    onChange={handleChange}
                                    min="0"
                                    placeholder="0"
                                    className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                />
                            </div>
                            <div>
                                <label className="text-sm font-medium text-foreground">Skill Level</label>
                                <select
                                    name="skill_level"
                                    value={formData.skill_level}
                                    onChange={handleChange}
                                    className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                >
                                    <option value="entry">Entry Level</option>
                                    <option value="intermediate">Intermediate</option>
                                    <option value="senior">Senior</option>
                                    <option value="expert">Expert</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium text-foreground">Location Preferred</label>
                                <input
                                    type="text"
                                    name="location_preferred"
                                    value={formData.location_preferred}
                                    onChange={handleChange}
                                    placeholder="e.g., Bangalore, Remote"
                                    className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                />
                            </div>
                        </div>

                        {/* Skills */}
                        <div>
                            <label className="text-sm font-medium text-foreground">Skills</label>
                            <input
                                type="text"
                                name="skills"
                                value={formData.skills}
                                onChange={handleChange}
                                placeholder="JavaScript, React, Node.js (comma-separated)"
                                className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                            />
                        </div>

                        {/* Salary & Availability */}
                        <div className="border-t border-border pt-4">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Salary & Availability</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-foreground">Current Salary</label>
                                    <input
                                        type="text"
                                        name="current_salary"
                                        value={formData.current_salary}
                                        onChange={handleChange}
                                        placeholder="e.g., ₹5,00,000 per annum"
                                        className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-foreground">Expected Salary</label>
                                    <input
                                        type="text"
                                        name="expected_salary"
                                        value={formData.expected_salary}
                                        onChange={handleChange}
                                        placeholder="e.g., ₹7,00,000 per annum"
                                        className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-foreground">Notice Period</label>
                                    <select
                                        name="notice_period"
                                        value={formData.notice_period}
                                        onChange={handleChange}
                                        className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                    >
                                        <option value="">Select notice period</option>
                                        <option value="immediate">Immediate</option>
                                        <option value="7_days">7 Days</option>
                                        <option value="15_days">15 Days</option>
                                        <option value="30_days">30 Days</option>
                                        <option value="45_days">45 Days</option>
                                        <option value="60_days">60 Days</option>
                                        <option value="90_days">90 Days</option>
                                        <option value="negotiable">Negotiable</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Previous Company */}
                        <div className="border-t border-border pt-4">
                            <h3 className="text-lg font-semibold text-foreground mb-4">Previous Company Details</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-sm font-medium text-foreground">Previous Company Name</label>
                                    <input
                                        type="text"
                                        name="previous_company"
                                        value={formData.previous_company}
                                        onChange={handleChange}
                                        placeholder="Company name"
                                        className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                    />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-foreground">Role/Position</label>
                                    <input
                                        type="text"
                                        name="previous_company_details"
                                        value={formData.previous_company_details}
                                        onChange={handleChange}
                                        placeholder="Your role at previous company"
                                        className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Resume Upload */}
                        <div>
                            <label className="text-sm font-medium text-foreground">Resume</label>
                            {resumePreview ? (
                                <div className="flex items-center justify-between p-3 mt-1 border border-border rounded-lg bg-muted/20">
                                    <div className="flex items-center gap-2 overflow-hidden">
                                        <Icon name="FileText" size={20} className="text-primary flex-shrink-0" />
                                        <span className="text-sm text-foreground truncate">{resumePreview}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={removeResume}
                                        className="text-error hover:text-error/80 flex-shrink-0 ml-2"
                                    >
                                        <Icon name="X" size={18} />
                                    </button>
                                </div>
                            ) : (
                                <label className="flex flex-col items-center justify-center w-full h-24 mt-1 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary hover:bg-muted/10 transition-colors">
                                    <div className="flex flex-col items-center justify-center pt-2 pb-3">
                                        <Icon name="UploadCloud" size={24} className="text-muted-foreground mb-1" />
                                        <p className="text-xs text-muted-foreground">Click to upload PDF or Word</p>
                                        <p className="text-xs text-muted-foreground">Max size: 5MB</p>
                                    </div>
                                    <input 
                                        type="file" 
                                        className="hidden" 
                                        accept=".pdf,.doc,.docx"
                                        onChange={handleFileChange}
                                    />
                                </label>
                            )}
                        </div>

                        {/* Notes */}
                        <div>
                            <label className="text-sm font-medium text-foreground">Additional Notes</label>
                            <textarea
                                name="notes"
                                value={formData.notes}
                                onChange={handleChange}
                                rows={3}
                                placeholder="Tell us more about yourself..."
                                className="w-full px-4 py-2 mt-1 rounded-lg border border-border bg-muted/20 focus:outline-none focus:ring-2 focus:ring-primary/50 text-foreground transition-all resize-none"
                            />
                        </div>

                        <div className="flex gap-3 pt-2">
                            <Button
                                disabled={loading || uploading}
                                type="submit"
                                variant="default"
                                className="flex-1 font-semibold"
                            >
                                {loading || uploading ? (
                                    <div className="flex items-center gap-2">
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                        {uploading ? 'Uploading...' : 'Submitting...'}
                                    </div>
                                ) : "Submit Application"}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onClose}
                                disabled={loading || uploading}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ApplyModal;